import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Calendar } from './ui/calendar';
import { CalendarIcon, X, Filter, RotateCcw } from 'lucide-react';
import { format, subDays, startOfWeek, startOfMonth, subMonths } from 'date-fns';

// Date Range Presets
const DATE_PRESETS = [
  { label: 'Today', getValue: () => ({ from: new Date(), to: new Date() }) },
  { label: 'Yesterday', getValue: () => ({ from: subDays(new Date(), 1), to: subDays(new Date(), 1) }) },
  { label: 'This Week', getValue: () => ({ from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: new Date() }) },
  { label: 'This Month', getValue: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
  { label: 'Last 7 Days', getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
  { label: 'Last 30 Days', getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { label: 'Last 3 Months', getValue: () => ({ from: subMonths(new Date(), 3), to: new Date() }) },
];

// Date Range Filter Component
export const DateRangeFilter = ({ value, onChange, label = "Date Range" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState(value || { from: null, to: null });

  const handlePresetClick = (preset) => {
    const range = preset.getValue();
    onChange(range);
    setIsOpen(false);
  };

  const handleApply = () => {
    onChange(tempRange);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange({ from: null, to: null });
    setTempRange({ from: null, to: null });
    setIsOpen(false);
  };

  const formatDateRange = () => {
    if (!value?.from) return 'Select dates';
    if (!value?.to || value.from.getTime() === value.to.getTime()) {
      return format(value.from, 'MMM d, yyyy');
    }
    return `${format(value.from, 'MMM d')} - ${format(value.to, 'MMM d, yyyy')}`;
  };

  return (
    <div className="flex flex-col gap-1">
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`justify-start text-left font-normal h-9 ${!value?.from && 'text-muted-foreground'}`}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formatDateRange()}
            {value?.from && (
              <X 
                className="ml-auto h-4 w-4 hover:text-red-500" 
                onClick={(e) => { e.stopPropagation(); handleClear(); }}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            {/* Presets */}
            <div className="border-r p-2 space-y-1">
              <p className="text-xs font-medium text-slate-500 px-2 mb-2">Quick Select</p>
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-sm h-8"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            {/* Calendar */}
            <div className="p-2">
              <p className="text-xs font-medium text-slate-500 px-2 mb-2">Custom Range</p>
              <Calendar
                mode="range"
                selected={tempRange}
                onSelect={setTempRange}
                numberOfMonths={2}
                className="rounded-md"
              />
              <div className="flex justify-end gap-2 mt-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={handleClear}>Clear</Button>
                <Button size="sm" onClick={handleApply}>Apply</Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Generic Select Filter Component
export const SelectFilter = ({ 
  label, 
  value, 
  onChange, 
  options, 
  placeholder = "All",
  allowAll = true 
}) => {
  return (
    <div className="flex flex-col gap-1">
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
        <SelectTrigger className="h-9 min-w-[140px]">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="all">All</SelectItem>}
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Search/Text Filter Component
export const SearchFilter = ({ label, value, onChange, placeholder = "Search..." }) => {
  return (
    <div className="flex flex-col gap-1">
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      <Input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 min-w-[150px]"
      />
    </div>
  );
};

// Filter Bar Container Component
export const FilterBar = ({ children, onReset, activeFiltersCount = 0 }) => {
  return (
    <div className="bg-slate-50 border rounded-lg p-3 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-700">Filters</span>
        {activeFiltersCount > 0 && (
          <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
            {activeFiltersCount} active
          </span>
        )}
        {activeFiltersCount > 0 && onReset && (
          <Button variant="ghost" size="sm" onClick={onReset} className="ml-auto h-7 text-xs">
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset All
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {children}
      </div>
    </div>
  );
};

// Helper to count active filters
export const countActiveFilters = (filters) => {
  return Object.values(filters).filter(v => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'object' && v.from === null && v.to === null) return false;
    if (typeof v === 'object' && v.from) return true;
    return true;
  }).length;
};

// Helper to filter data by date range
export const filterByDateRange = (data, dateField, dateRange) => {
  if (!dateRange?.from) return data;
  
  const fromDate = new Date(dateRange.from);
  fromDate.setHours(0, 0, 0, 0);
  
  const toDate = dateRange.to ? new Date(dateRange.to) : new Date(dateRange.from);
  toDate.setHours(23, 59, 59, 999);
  
  return data.filter(item => {
    const itemDate = new Date(item[dateField]);
    return itemDate >= fromDate && itemDate <= toDate;
  });
};

export default {
  DateRangeFilter,
  SelectFilter,
  SearchFilter,
  FilterBar,
  countActiveFilters,
  filterByDateRange
};
